import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import styled from 'styled-components';
import { FiChevronRight } from 'react-icons/fi';
import useCoordinatedDropdown from '../../hooks/useCoordinatedDropdown';
import { DropDownMenu, Element, MenuItemIcon, MenuItemLabel, MESSAGE_MENU_Z_INDEX } from './MessageContextMenu';

export default function CascadingSubmenu({ label, icon, children }) {
    const [open, setOpen] = useCoordinatedDropdown();
    const [trigger, setTrigger] = useState(null);
    const [panel, setPanel] = useState(null);
    const closeTimer = useRef(null);
    const focusOnOpen = useRef(false);
    const menuId = React.useId();
    const menuItems = () => [...(panel?.querySelectorAll('button:not(:disabled), [role="menuitem"]:not([aria-disabled="true"])') || [])];
    const { styles, attributes } = usePopper(trigger, panel, {
        strategy: 'fixed',
        placement: 'right-start',
        modifiers: [
            { name: 'offset', options: { offset: [0, 4] } },
            { name: 'flip', options: { fallbackPlacements: ['left-start'], padding: 8 } },
            { name: 'preventOverflow', options: { padding: 8, altAxis: true, tether: false } }
        ]
    });
    const cancelClose = () => clearTimeout(closeTimer.current);
    const close = (focus = false) => {
        cancelClose();
        focusOnOpen.current = false;
        setOpen(false);
        if (focus) trigger?.focus();
    };
    const show = () => { cancelClose(); setOpen(true); };
    const scheduleClose = () => {
        cancelClose();
        closeTimer.current = setTimeout(() => close(), 180);
    };
    useEffect(() => () => clearTimeout(closeTimer.current), []);
    useEffect(() => {
        if (open && panel && focusOnOpen.current) {
            menuItems()[0]?.focus();
            focusOnOpen.current = false;
        }
    }, [open, panel]);

    return (
        <li onClick={event => event.stopPropagation()}
            onMouseEnter={show} onMouseLeave={scheduleClose}
            onBlur={event => {
                if (!trigger?.contains(event.relatedTarget) && !panel?.contains(event.relatedTarget)) close();
            }}>
            <SubmenuButton as="button" type="button" ref={setTrigger}
                aria-expanded={open} aria-controls={open ? menuId : undefined}
                data-submenu-open={open}
                onClick={show}
                onKeyDown={event => {
                    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                        event.preventDefault();
                        focusOnOpen.current = true;
                        show();
                        if (panel) {
                            menuItems()[0]?.focus();
                            focusOnOpen.current = false;
                        }
                    } else if (event.key === 'Escape' && open) {
                        event.preventDefault();
                        event.stopPropagation();
                        close(true);
                    }
                }}>
                <MenuItemLabel><MenuItemIcon>{icon}</MenuItemIcon>{label}</MenuItemLabel>
                <FiChevronRight size={14} />
            </SubmenuButton>
            {open ? createPortal(
                <Panel ref={setPanel} id={menuId} data-cascading-submenu
                    style={styles.popper} {...attributes.popper}
                    onMouseEnter={cancelClose} onMouseLeave={scheduleClose}
                    onKeyDown={event => {
                        if (['ArrowLeft', 'Escape'].includes(event.key)) {
                            event.preventDefault();
                            event.stopPropagation();
                            close(true);
                        } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
                            event.preventDefault();
                            const buttons = menuItems();
                            const index = buttons.indexOf(document.activeElement);
                            const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 :
                                (index + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
                            buttons[next]?.focus();
                        } else if (['Enter', ' '].includes(event.key) && event.target.getAttribute('role') === 'menuitem' && event.target.tagName !== 'BUTTON') {
                            event.preventDefault();
                            event.target.click();
                        }
                    }}>
                    <ul aria-label={label}>{children}</ul>
                </Panel>, document.body
            ) : null}
        </li>
    );
}

export const SubmenuButton = styled(Element)`
    box-sizing: border-box;
    width: 100%;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    &[aria-expanded='true'] { background: rgba(255, 255, 255, 0.08); }
    &:focus-visible { outline: 2px solid rgba(255, 255, 255, 0.6); outline-offset: -2px; }
`;
const Panel = styled(DropDownMenu)`
    z-index: ${MESSAGE_MENU_Z_INDEX + 1};
    width: min(220px, calc(100vw - 16px));
    min-width: 0;
    max-height: calc(100vh - 16px);
    overflow-y: auto;
    ul { margin: 0; padding: 4px 0; list-style: none; }
`;
